import React from "react";
import Typography from "ui/Typography";
import Tip from "components/Tip";
import Form from "ui/Form";
import Checkbox from "ui/Checkbox";
import Link from "ui/Link";
import Button from "ui/Button";
import { Formik } from "formik";
import * as yup from "yup";
import FormError from "ui/FormError";
import styled from "styled-components/macro";
import questionMarkMint from "images/question_mark_mint.png";
import questionMarkMint2x from "images/question_mark_mint_2x.png";
import messageBubbleCarrot from "images/message_bubble_carrot.png";
import messageBubbleCarrot2x from "images/message_bubble_carrot_2x.png";
import thumbsUpSun from "images/thumbs_up_sun.png";
import thumbsUpSun2x from "images/thumbs_up_sun_2x.png";

type WelcomeScreenProps = {
  initialTermsOfServiceChecked: boolean;
  onNext: () => void;
  onTermsOfServiceChange: (checked: boolean) => void;
};

const STUDENT_TOS_SCHEMA = yup.object().shape({
  acceptedTos: yup
    .boolean()
    .oneOf([true], "Please accept the terms and conditions."),
});

const $WelcomeScreenContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const $TosContainer = styled.div`
  display: flex;
  align-items: flex-start;
  padding: ${({ theme }) => theme.SizeXs};

  label {
    margin-bottom: 0;
    align-self: center;
  }
`;

const $TosStudentAgreement = styled($TosContainer)`
  margin-top: -1rem;
`;

const $FormLabel = styled(Form.Label)`
  margin-left: 0.5rem;
`;

const $Form = styled(Form)`
  background-color: ${({ theme }) => theme.ColorBlurple100};
  border-radius: 1rem;
  padding: 2rem;
  margin-bottom: 2rem;
`;

export default function WelcomeScreen({
  initialTermsOfServiceChecked,
  onNext,
  onTermsOfServiceChange,
}: WelcomeScreenProps) {
  const studentTips = [
    {
      tip: "Watch for questions from your teacher",
      description: "Each question helps you get to know each other.",
      source: questionMarkMint,
      sourceSet: `${questionMarkMint} 1x, ${questionMarkMint2x} 2x`,
    },
    {
      tip: "Share your thoughts and feelings",
      description: "Only your teacher can see what you say.",
      source: messageBubbleCarrot,
      sourceSet: `${messageBubbleCarrot} 1x, ${messageBubbleCarrot2x} 2x`,
    },
    {
      tip: "Be heard",
      description: "Know that your teacher is listening to you.",
      source: thumbsUpSun,
      sourceSet: `${thumbsUpSun} 1x, ${thumbsUpSun2x} 2x`,
    },
  ];

  return (
    <$WelcomeScreenContainer data-testid="welcomeScreen">
      <Typography size="h2" as="h2" fontWeight="normal">
        It&rsquo;s so easy to use Along
      </Typography>

      <div>
        {studentTips.map(({ tip, source, sourceSet, description }) => (
          <Tip
            key={tip}
            tip={tip}
            source={source}
            sourceSet={sourceSet}
            description={description}
          />
        ))}
      </div>

      <Formik
        initialValues={{
          acceptedTos: initialTermsOfServiceChecked,
        }}
        onSubmit={onNext}
        validationSchema={STUDENT_TOS_SCHEMA}
        handleChange
        handleBlur
        validateOnChange={true}
      >
        {({ handleSubmit, handleBlur, handleChange, errors, values }) => (
          <$Form>
            <Form.Group controlId="acceptedTos">
              <$TosContainer>
                <Typography size="tooltip" color="ColorSpace500">
                  You can read more about our privacy practices in our{" "}
                  <Link
                    href="/student-privacy-notice"
                    opensInNewTab
                    rel="noreferrer"
                    analyticsName="clicked_student_privacy_notice_link"
                  >
                    Student Privacy Notice
                  </Link>{" "}
                  and{" "}
                  <Link
                    href="/user-agreement"
                    opensInNewTab
                    rel="noreferrer"
                    analyticsName="clicked_user_agreement_link"
                  >
                    User Agreement
                  </Link>
                  . Along has a{" "}
                  <Link
                    href="/code-of-conduct"
                    opensInNewTab
                    rel="noreferrer"
                    analyticsName="clicked_code_of_conduct_link"
                  >
                    Code of Conduct
                  </Link>{" "}
                  that we expect all users to follow.
                </Typography>
              </$TosContainer>
              <$TosStudentAgreement>
                <Checkbox
                  checked={values.acceptedTos}
                  name="acceptedTos"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    onTermsOfServiceChange(event.target.checked);
                    handleChange(event);
                  }}
                  onBlur={handleBlur}
                  required
                  aria-required
                  aria-invalid={!!errors.acceptedTos}
                  aria-describedby="acceptedTosError"
                />
                <$FormLabel>
                  <Typography size="tooltip" color="ColorSpace500">
                    I have reviewed the Along{" "}
                    <Link
                      href="/code-of-conduct"
                      opensInNewTab
                      rel="noreferrer"
                      analyticsName="clicked_code_of_conduct_link"
                    >
                      Code of Conduct
                    </Link>
                  </Typography>
                </$FormLabel>
              </$TosStudentAgreement>
              <FormError id="acceptedTosError" isInvalid={!!errors.acceptedTos}>
                {errors.acceptedTos}
              </FormError>
            </Form.Group>

            <Button
              block
              disabled={!values.acceptedTos}
              analyticsName="student_accepted_terms_of_service"
              variant="primary"
              onClick={() => handleSubmit()}
              requestInProgress={false}
            >
              Next
            </Button>
          </$Form>
        )}
      </Formik>
    </$WelcomeScreenContainer>
  );
}
